import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  CheckCircle2, Download, Home, Phone, Mail, 
  Wifi, Clock, Calendar, FileText, PartyPopper
} from 'lucide-react';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const referenceId = searchParams.get('ref');

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/checkout/payment-status/${referenceId}`);
        const data = await response.json();
        setPaymentDetails(data);
      } catch (err) {
        console.error('Error fetching payment details:', err);
      } finally {
        setLoading(false);
      }
    };

    if (referenceId) {
      fetchPaymentDetails();
    } else {
      setLoading(false);
    }
  }, [referenceId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-900 via-green-800 to-emerald-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-900 via-green-800 to-emerald-900 py-10 px-6">
      {/* Confetti Animation */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-fall"
            style={{
              left: `${Math.random() * 100}%`,
              top: `-20px`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          >
            <div 
              className="w-3 h-3 rounded-full"
              style={{
                backgroundColor: ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'][Math.floor(Math.random() * 5)]
              }}
            />
          </div>
        ))}
      </div>

      <div className="max-w-2xl mx-auto relative z-10">
        {/* Success Animation */}
        <div className="text-center mb-8">
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-green-400 rounded-full blur-xl opacity-50 animate-pulse"></div>
            <div className="relative w-24 h-24 mx-auto bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-2xl">
              <CheckCircle2 className="h-12 w-12 text-white" />
            </div>
          </div>
          <div className="mt-6 flex items-center justify-center gap-2">
            <PartyPopper className="h-6 w-6 text-yellow-400" />
            <h1 className="text-3xl font-black text-white">Payment Successful!</h1>
            <PartyPopper className="h-6 w-6 text-yellow-400" />
          </div>
          <p className="text-green-200 mt-2">Thank you for choosing Netix Fibernet</p>
        </div>

        {/* Payment Details Card */}
        <Card className="border-0 bg-white/10 backdrop-blur-sm mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Payment Receipt
            </h2>
          </div>
          <CardContent className="p-6 space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-white/10">
              <span className="text-gray-400">Reference ID</span>
              <span className="text-white font-mono font-bold">{referenceId || 'N/A'}</span>
            </div>
            
            {paymentDetails && (
              <>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-gray-400">Plan</span>
                  <span className="text-white font-bold">{paymentDetails.plan_name} Plan</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-gray-400">Amount Paid</span>
                  <span className="text-2xl text-green-400 font-black">₹{paymentDetails.amount}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-gray-400">Status</span>
                  <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4" />
                    {paymentDetails.status === 'completed' ? 'Completed' : 'Processing'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-gray-400">Date</span>
                  <span className="text-white">{new Date().toLocaleDateString('en-IN', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* What's Next Card */}
        <Card className="border-0 bg-white/10 backdrop-blur-sm mb-6">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-400" />
              What Happens Next?
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-400 font-bold">1</span>
                </div>
                <div>
                  <h4 className="text-white font-semibold">Confirmation Email</h4>
                  <p className="text-gray-400 text-sm">You'll receive a confirmation email with your order details within 5 minutes.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-green-400 font-bold">2</span>
                </div>
                <div>
                  <h4 className="text-white font-semibold">Installation Scheduling</h4>
                  <p className="text-gray-400 text-sm">Our team will contact you within 24 hours to schedule your FREE installation.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-400 font-bold">3</span>
                </div>
                <div>
                  <h4 className="text-white font-semibold">Get Connected</h4>
                  <p className="text-gray-400 text-sm">Enjoy blazing-fast internet! Our technician will complete installation in 2-3 hours.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Support Card */}
        <Card className="border-0 bg-gradient-to-r from-red-500/20 to-pink-500/20 backdrop-blur-sm mb-6">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold text-white mb-4">Need Help?</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <a 
                href="tel:+919319919988"
                data-testid="payment-success-mobile-link"
                className="flex items-center gap-3 bg-white/10 rounded-xl p-4 hover:bg-white/20 transition-colors"
              >
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <Phone className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-white font-semibold">Call Us</div>
                  <div className="text-gray-400 text-sm">+91 93199 19988</div>
                </div>
              </a>
              <a
                href="tel:06276459015"
                data-testid="payment-success-office-link"
                className="flex items-center gap-3 bg-white/10 rounded-xl p-4 hover:bg-white/20 transition-colors"
              >
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                  <Phone className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-white font-semibold">Office Phone</div>
                  <div className="text-gray-400 text-sm">06276 - 459015</div>
                </div>
              </a>
              <a 
                href="mailto:care@netixfibernet.com"
                className="flex items-center gap-3 bg-white/10 rounded-xl p-4 hover:bg-white/20 transition-colors"
              >
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                  <Mail className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-white font-semibold">Email Us</div>
                  <div className="text-gray-400 text-sm">care@netixfibernet.com</div>
                </div>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            onClick={() => navigate('/')}
            className="flex-1 h-12 bg-white text-gray-900 hover:bg-gray-100 font-bold"
          >
            <Home className="h-5 w-5 mr-2" />
            Back to Home
          </Button>
          <Button
            onClick={() => window.print()}
            variant="outline"
            className="flex-1 h-12 border-white/30 text-white hover:bg-white/10 font-bold"
          >
            <Download className="h-5 w-5 mr-2" />
            Download Receipt
          </Button>
        </div>
      </div>

      {/* CSS for confetti animation */}
      <style jsx>{`
        @keyframes fall {
          0% {
            transform: translateY(-20px) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-fall {
          animation: fall linear forwards;
        }
      `}</style>
    </div>
  );
};

export default PaymentSuccess;
